// @rpg-unit/adaptive-music: music that follows the game (tier 4; composition framework, decision 0077), cut from spike
// 002's audio wiring (src/audio/index.ts and content.ts, 2026-10-20). Two halves:
//
//   - the score: `resolveScore(pick, rows, knobs)` turns preset ids (moods, a fight layer, a sting) into @rpg/audio's
//     director table and instruments, resolving every instrument and band the presets name by id (the presets can't
//     import each other: rule 4) and refusing a score whose ids don't resolve or whose moods disagree with their band
//     (`scoreIssues`). `orchestrate(mood, band)` re-voices a mood for another band;
//   - the conductor: `createAdaptiveMusic({ source, clock, moods })` drives any @rpg/contracts `MusicSource` from what
//     the game knows: the place (area, time of day: a time's mood wins over an area's, else the default), the fight
//     (engaged: the combat state, its layer in on the bar; won: the victory phrase on the bar line; else exploring) and
//     an intensity dial (0 calm to 1 urgent; while exploring, at or past the source's threshold the fight layer comes
//     in). With @rpg/audio the source is `directorMusicSource(engine.music.director(), table)` and the clock
//     `engine.now()`: the engine schedules its own director, so the conductor only asks.
//
// Seams (features meet at contracts, never each other): it consumes the place, the fight and an intensity, all as
// calls (no event of its own); it exposes the source's state and bar grid (for stingers and debug views), and as a
// kernel module it provides the "music" service (`ADAPTIVE_MUSIC_DECLARATIONS`).
import { directorMusicSource, type Instrument, type MusicDirector, type MusicTable } from "@rpg/audio";
import { clampIntensity, type MusicSource, type MusicSourceState, type MusicStateName } from "@rpg/contracts";
import { ENSEMBLE_PRESETS_TABLE, type EnsemblePreset, type EnsembleRole } from "@rpg-unit/ensemble-presets";
import { INSTRUMENT_PRESETS_TABLE, instrumentsFor, type InstrumentPreset } from "@rpg-unit/instrument-presets";
import { MUSIC_COMBAT_TABLE, MUSIC_MOODS_TABLE, MUSIC_STINGS_TABLE, toCombat, toMood, toVictory, tuneMood, type CombatPreset, type MoodKnobs, type MoodPreset, type StingPreset } from "@rpg-unit/music-mood-presets";

/** This unit's version (the manifest's and package.json's). */
export const ADAPTIVE_MUSIC_VERSION = "0.1.0" as const;

/** What the feature declares as a kernel module (`GameModule.provides`/`needs`): it offers the music service and
 *  needs nothing (the game calls it). */
export const ADAPTIVE_MUSIC_DECLARATIONS = { provides: { services: ["music"] }, needs: {} } as const;

/** Every preset row the score draws on. */
export interface ScoreRows {
  moods: readonly MoodPreset[];
  combat: readonly CombatPreset[];
  stings: readonly StingPreset[];
  instruments: readonly InstrumentPreset[];
  ensembles: readonly EnsemblePreset[];
}

/** The presets' own tables (a game that loads them through its content passes its registry's rows instead). */
export const PRESET_ROWS: ScoreRows = {
  moods: MUSIC_MOODS_TABLE.rows,
  combat: MUSIC_COMBAT_TABLE.rows,
  stings: MUSIC_STINGS_TABLE.rows,
  instruments: INSTRUMENT_PRESETS_TABLE.rows,
  ensembles: ENSEMBLE_PRESETS_TABLE.rows,
};

/** What a game picks: its moods, its fight layer and its victory sting (preset ids), and optionally a band per mood to
 *  re-voice it with (an ensemble preset's id). */
export interface ScorePick {
  moods: readonly string[];
  combat: string;
  victory: string;
  bands?: Readonly<Record<string, string>>;
}

/** The feature's knobs: the moods' (pace, key, looseness, swell), the instruments' (room, light). */
export interface ScoreKnobs extends MoodKnobs {
  /** Scales the instruments' room and echo sends (drier or wetter). */
  room?: number;
  /** The phone's roots (lighter to load). */
  light?: boolean;
}

/** The director's table and instruments, ready for `createAudio({ music: { table, instruments, … } })`. */
export interface Score {
  table: MusicTable;
  instruments: Record<string, Instrument>;
}

/** The perceptual knobs (rule 13). */
export const ADAPTIVE_MUSIC_KNOBS = {
  intensity: "calmer or more urgent: the dial (0..1); while exploring, at or past `fightAt` the fight layer comes in on the bar",
  fightAt: "how soon the fight music comes in: the intensity at which exploring brings the fight layer in (default 0.75)",
  band: "played by another band: re-voice a mood for an ensemble preset (`pick.bands`)",
  pace: "faster or slower (every mood)",
  key: "higher or lower (every mood, every part's range with it)",
  looseness: "tighter or looser (every mood's humanising)",
  swell: "more or less dynamic (every mood's swell)",
  room: "drier or wetter (every instrument's sends)",
  light: "lighter to load (the phone's roots)",
} as const;

const PART_ROLES = [
  ["lead", "lead"],
  ["pad", "pad"],
  ["arp", "arp"],
  ["bass", "bass"],
] as const;

/** A mood re-voiced for a band: each part played by the band's instrument in its role, a section's other colour by
 *  its `alt`; a part whose role the band lacks drops out (and a section's other colour falls back to the tune's). The
 *  mood names the band afterwards. */
export function orchestrate(mood: MoodPreset, band: EnsemblePreset): MoodPreset {
  const out = structuredClone(mood);
  for (const [part, role] of PART_ROLES) {
    const inst = band.roles[role];
    if (!out[part]) continue;
    if (inst) out[part] = { ...out[part]!, inst } as never;
    else delete out[part];
  }
  if (out.form) out.form = out.form.map((s) => (s.inst === undefined ? s : band.roles.alt ? { ...s, inst: band.roles.alt } : (({ inst: _i, ...rest }) => rest)(s)));
  out.ensemble = band.id;
  return out;
}

/** What's wrong with a pick against the rows (empty: it resolves): every id names a row; every band a mood or layer
 *  names exists; each mood's parts agree with its band (the tune, the other colour, the pad, the arpeggio, the bass);
 *  the fight layer's parts with its band's roles; the sting's instrument with its band's; every instrument named
 *  exists; the moods share one key centre. */
export function scoreIssues(pick: ScorePick, rows: ScoreRows = PRESET_ROWS): string[] {
  const out: string[] = [];
  const band = (id: string): EnsemblePreset | undefined => rows.ensembles.find((e) => e.id === id);
  const inst = new Set(rows.instruments.map((i) => i.id));
  const need = (owner: string, id: string | undefined): void => {
    if (id && !inst.has(id)) out.push(`${owner}: no instrument "${id}"`);
  };
  const moods: MoodPreset[] = [];
  for (const id of pick.moods) {
    let m = rows.moods.find((x) => x.id === id);
    if (!m) {
      out.push(`no mood "${id}"`);
      continue;
    }
    const over = pick.bands?.[id];
    if (over) {
      const b = band(over);
      if (!b) {
        out.push(`${id}: no band "${over}" to play it`);
        continue;
      }
      m = orchestrate(m, b);
    }
    moods.push(m);
    const b = band(m.ensemble);
    if (!b) out.push(`${id}: no band "${m.ensemble}"`);
    for (const [part, role] of PART_ROLES) {
      const p = m[part];
      if (!p) continue;
      need(`${id}'s ${part}`, p.inst);
      if (b && b.roles[role] !== p.inst) out.push(`${id}: its ${part} plays ${p.inst}, its band ${b.id}'s ${role} is ${b.roles[role] ?? "nobody"}`);
    }
    for (const s of m.form ?? [])
      if (s.inst) {
        need(`${id}'s form`, s.inst);
        if (b && b.roles.alt !== s.inst) out.push(`${id}: a section's tune on ${s.inst}, its band ${b.id}'s alt is ${b.roles.alt ?? "nobody"}`);
      }
  }
  const c = rows.combat.find((x) => x.id === pick.combat);
  if (!c) out.push(`no fight layer "${pick.combat}"`);
  else {
    const b = band(c.ensemble);
    if (!b) out.push(`${c.id}: no band "${c.ensemble}"`);
    for (const p of c.parts) {
      need(`${c.id}'s ${p.role}`, p.inst);
      if (b && b.roles[p.role as EnsembleRole] !== p.inst) out.push(`${c.id}: its ${p.role} plays ${p.inst}, its band ${b.id}'s is ${b.roles[p.role as EnsembleRole] ?? "nobody"}`);
    }
  }
  const v = rows.stings.find((x) => x.id === pick.victory);
  if (!v) out.push(`no sting "${pick.victory}"`);
  else {
    need(v.id, v.inst);
    const b = band(v.ensemble);
    if (!b) out.push(`${v.id}: no band "${v.ensemble}"`);
    else if (b.roles.sting !== v.inst) out.push(`${v.id}: plays ${v.inst}, its band ${b.id}'s sting is ${b.roles.sting ?? "nobody"}`);
  }
  if (new Set(moods.map((m) => m.root)).size > 1) out.push(`the moods sit on ${new Set(moods.map((m) => m.root)).size} key centres: a change would clash`);
  return out;
}

/** The instruments a pick plays, each once, in the order they are first named (moods' parts and sections, the fight
 *  layer, the sting). */
function instrumentsOf(table: MusicTable): Set<string> {
  const ids = new Set<string>();
  for (const m of table.moods) {
    for (const p of [m.lead, m.pad, m.arp, m.bass]) if (p) ids.add(p.inst);
    for (const s of m.form ?? []) if (s.inst) ids.add(s.inst);
  }
  for (const p of table.combat.parts) ids.add(p.inst);
  ids.add(table.victory.inst);
  return ids;
}

/** Resolve a pick into the director's table and instruments (in the instrument rows' order). Throws, naming every
 *  problem, when `scoreIssues` finds any. With no knobs the table's moods are the presets' musical fields exactly. */
export function resolveScore(pick: ScorePick, rows: ScoreRows = PRESET_ROWS, knobs: ScoreKnobs = {}): Score {
  const issues = scoreIssues(pick, rows);
  if (issues.length) throw new Error(`adaptive music: the score doesn't resolve:\n  ${issues.join("\n  ")}`);
  const { room, light, ...moodKnobs } = knobs;
  const tuned = Object.values(moodKnobs).some((v) => v !== undefined);
  const moods = pick.moods.map((id) => {
    let m = rows.moods.find((x) => x.id === id)!;
    const over = pick.bands?.[id];
    if (over) m = orchestrate(m, rows.ensembles.find((e) => e.id === over)!);
    return toMood(tuned ? tuneMood(m, moodKnobs) : m);
  });
  const table: MusicTable = { moods, combat: toCombat(rows.combat.find((x) => x.id === pick.combat)!), victory: toVictory(rows.stings.find((x) => x.id === pick.victory)!) };
  const instruments = instrumentsFor(rows.instruments, instrumentsOf(table), { ...(room !== undefined ? { room } : {}), ...(light ? { light } : {}) });
  return { table, instruments };
}

/** Moods by place: a time of day's mood wins over an area's, else the default. */
export interface MoodMap {
  default: string;
  areas: Readonly<Record<string, string | undefined>>;
  times: Readonly<Record<string, string | undefined>>;
}

/** The mood for a place and time: a time's mood wins over the area's, else the default. */
export function moodFor(map: MoodMap, place: { area: string | null; time: string | null }): string {
  if (place.time && map.times[place.time]) return map.times[place.time]!;
  if (place.area && map.areas[place.area]) return map.areas[place.area]!;
  return map.default;
}

/** The conductor. */
export interface AdaptiveMusic {
  /** The source it drives. */
  readonly source: MusicSource;
  /** Where the listener is: the mood follows (on the source's next transition point). Returns the mood asked for. */
  place(p: { area: string | null; time: string | null }): string;
  /** The fight: `engaged` brings the combat state (the fight layer on the bar); leaving it after a win (`won`) plays
   *  the victory phrase on the bar line, else back to exploring. Only a change does anything. Returns the state. */
  fight(f: { engaged: boolean; won?: boolean }): MusicStateName;
  /** Turn the intensity dial (clamped to 0..1); only a change reaches the source. */
  intensity(level: number): void;
  /** Schedule through the source (`source.update(clock())`): for a source nothing else schedules. The audio engine
   *  schedules its own director, so a game on @rpg/audio never calls this. */
  update(): void;
  /** The source's state, and the place and mood the conductor last asked for. */
  state(): MusicSourceState & { place: { area: string | null; time: string | null }; asked: string | null };
}

/** Drive a music source from the place, the fight and an intensity, on `clock` (the source's time: with @rpg/audio,
 *  `engine.now()`). */
export function createAdaptiveMusic(o: { source: MusicSource; clock: () => number; moods: MoodMap }): AdaptiveMusic {
  const { source, clock } = o;
  let place: { area: string | null; time: string | null } = { area: null, time: null };
  let asked: string | null = null;
  let engaged = false;
  let state: MusicStateName = "explore";
  let level = 0;
  return {
    source,
    place(p) {
      place = { area: p.area, time: p.time };
      asked = moodFor(o.moods, place);
      source.setMood(asked, clock());
      return asked;
    },
    fight(f) {
      if (f.engaged === engaged) return state;
      engaged = f.engaged;
      state = engaged ? "combat" : f.won ? "victory" : "explore";
      source.setState(state, clock());
      return state;
    },
    intensity(x) {
      const next = clampIntensity(x);
      if (next === level) return;
      level = next;
      source.setIntensity(level, clock());
    },
    update() {
      source.update(clock());
    },
    state: () => ({ ...source.state(), place: { ...place }, asked }),
  };
}

/** The conductor over @rpg/audio's director (the engine's, `engine.music.director()`), on the engine's clock: the
 *  director's music source with the fight layer coming in at `fightAt` while exploring. */
export function directorMusic(o: { director: MusicDirector; table: MusicTable; clock: () => number; moods: MoodMap; fightAt?: number }): AdaptiveMusic {
  return createAdaptiveMusic({ source: directorMusicSource(o.director, o.table, { combatAt: o.fightAt ?? 0.75 }), clock: o.clock, moods: o.moods });
}
